/**
 * TrialBanner — restrained dashboard banner for tenants in trial / expired /
 * suspended / cancelled state.
 *
 * Shown to every authenticated user (Owners and Operators alike) so the
 * trial countdown and post-trial messaging are consistent across roles. Tone
 * is calm and operational — no consumer-style upsell pulses. Styling matches
 * the existing ProviderHealthBanner (single static dot, soft tint, sits at
 * the top of the dashboard).
 *
 * Visibility logic:
 *   - active        → null (no banner)
 *   - trial         → soft amber: "X days left in trial"
 *   - expired       → soft slate: "Trial ended. Workspace moved to Starter."
 *   - suspended     → soft red:   "Workspace suspended. Contact your administrator."
 *   - cancelled     → soft red:   "Workspace cancelled. Contact your administrator."
 */
import React from 'react';
import { useFeatures } from '../context/FeatureContext';
import { useAuth } from '../context/AuthContext';

export default function TrialBanner() {
  const { ready, activationStatus, effectiveStatus, daysRemaining, trialEndsAt, planLabel } = useFeatures();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission && (hasPermission('integration:view') || hasPermission('integration:manage'));

  if (!ready) return null;
  if (!activationStatus || activationStatus === 'active') return null;

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }) : null;

  let tone, message, testid;
  if (activationStatus === 'trial' && effectiveStatus === 'trial') {
    tone = { bg: '#fef3c7', border: '#fde68a', dot: '#b45309', text: '#92400e' };
    const days = Number.isFinite(daysRemaining) ? daysRemaining : null;
    const dayWord = days === 1 ? 'day' : 'days';
    if (days === 0) {
      message = `Trial ends today${trialEndsAt ? ' (' + fmt(trialEndsAt) + ')' : ''}. Contact your administrator to keep current access.`;
    } else if (days != null) {
      message = `${days} ${dayWord} left in your trial${trialEndsAt ? ` — ends ${fmt(trialEndsAt)}` : ''}. After that the workspace moves to Starter access.`;
    } else {
      message = 'Trial active. Contact your administrator for plan details.';
    }
    testid = 'trial-banner-trial';
  } else if (effectiveStatus === 'expired' || activationStatus === 'expired') {
    tone = { bg: '#f1f5f9', border: '#e2e8f0', dot: '#64748b', text: '#475569' };
    message = `Trial ended. Your workspace has moved to ${planLabel || 'Starter'} access. Contact your administrator to upgrade.`;
    testid = 'trial-banner-expired';
  } else if (activationStatus === 'suspended') {
    tone = { bg: '#fee2e2', border: '#fecaca', dot: '#b91c1c', text: '#991b1b' };
    message = 'This workspace is currently suspended. Contact your administrator.';
    testid = 'trial-banner-suspended';
  } else if (activationStatus === 'cancelled') {
    tone = { bg: '#fee2e2', border: '#fecaca', dot: '#b91c1c', text: '#991b1b' };
    message = 'This workspace has been cancelled. Contact your administrator if you believe this is in error.';
    testid = 'trial-banner-cancelled';
  } else {
    return null;
  }

  return (
    <div
      data-testid={testid}
      role="status"
      aria-live="polite"
      style={{
        margin: '8px 0 12px',
        padding: '10px 14px',
        borderRadius: 6,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        color: tone.text,
      }}
    >
      <span aria-hidden style={{
        width: 8, height: 8, borderRadius: '50%', background: tone.dot, flex: '0 0 auto',
      }} />
      <span style={{ flex: 1 }}>{message}</span>
      {/* Admins get a second-tier link to Settings; non-admins keep the message minimal. */}
      {isAdmin && (
        <a
          href="/settings"
          data-testid="trial-banner-settings-link"
          style={{
            background: 'transparent', border: `1px solid ${tone.border}`,
            color: tone.text, fontSize: 12, fontWeight: 600,
            padding: '4px 10px', borderRadius: 4, textDecoration: 'none',
          }}
        >
          Open Settings
        </a>
      )}
    </div>
  );
}
